<template>
  <div class="users-list fixed-heading" :class="{ scrollable: fullScroll }">
    <div class="heading">
      <h1>User management</h1>
      <p class="guide_text">Add or remove user profiles.</p>
      <ds-input
        v-model="filters.q" placeholder="Search by username, email or claim..."
        style="margin-top:9px"
      />
      <br>
      <div class="users__cta-container">
        <div class="users__button-group">
          <ds-button variant="primary" icon="plus" size="small" label="Add an ecosystem member" @click="showAddUserModal" v-if="canAddEcosystemMembers"/>
          <ds-button variant="primary" icon="plus" size="small" label="Invite admins" v-if="isOwner" @click="showAddUserPanel"/>
          <ds-button v-if="isOwner" size="small" icon="download" label="Export user list" @click="exportCsv"/>
          <community-dropdown v-if="hasAccessToNewCommunities" :initial-community-id="filters.community" @selected-community-changed="updateCommunityFilter"/>
        </div>

        <div class="users__results-text">
          Results: {{ numberOfResults !== null ? ' ' + numberOfResults + ' users' : '' }}
          {{ filtersAsString }}
          <template v-if="filtersAsString">
            - <span @click="clearFilters" class="a">clear filters</span>
          </template>
        </div>
      </div>
    </div>
    <div v-if="loading">
      <loading/>
    </div>
    <div :class="fullScroll ? 'not-scrollable' : 'scrollable'" style="overflow-x: auto;" v-else>
      <table class="table" style="border: 0px; word-break: break-all;">
        <thead>
        <tr>
          <th-sort by="name" :options="filterOptions.verified" v-model="filters.verified">
            Name
          </th-sort>
          <th-sort by="role" :options="filterOptions.role" v-model="filters.role">Role
          </th-sort>
          <th-sort by="email">Email</th-sort>
          <th>Actions</th>
          <th v-if="displayConversationColumn">Conversation</th>
          <th v-if="displaySubscribedToColumn" style="white-space: nowrap">Subscribed to</th>
          <th-sort
            v-if="displayActiveSubscriptionsColumn" by="active_subscriptions"
            :options="filterOptions.activeSubscription" v-model="filters.active_subscription"
            style="white-space: nowrap"
          >
            Active subscriptions
          </th-sort>
          <th-sort by="subscribed_plan" v-if="displayPackagePlanColumn">Package
            Plan
          </th-sort>
          <th-sort v-if="displayLastLoginColumn" by="last_activity">Last login</th-sort>
          <th-sort by="communities">Communities</th-sort>
          <th-sort by="content_activity" v-if="displayContentActivityColumn">Content</th-sort>
          <th-sort v-if="displayAnnouncementActivityColumn" by="announcement_activity">{{ announcementsLabel }}</th-sort>
          <th-sort v-if="displayActorActivityColumn" by="actor_activity">Actors</th-sort>
          <th-sort v-if="displayLastInvitationSentColumn" by="created_at">Last invitation sent</th-sort>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="user in users"
          @click="openUserSidePanel(user)"
          style="cursor: pointer;"
        >
          <template v-if="! user.invited_user">
            <td>
              {{ user.name }}
              <badge
                v-if="user.team_verified === false" name="Needs Curation"
                size="extra-small" style="float:right;"
              />
              <badge
                v-if="! user.verified" name="Unverified Email" size="extra-small"
                style="float:right;margin-right:5px"
              />
            </td>
            <td>
              <template v-if="! canEditUser(user)">
                {{ normaliseRole(user.role) }}
              </template>
              <a v-else href="#" @click.stop="showUserEditModal(user, 'edit')">{{
                  normaliseRole(user.role)
                }}</a>
            </td>
            <td><a :href="'mailto:' + user.email">{{ user.email }}</a></td>
            <td>
              <div class="users__action-buttons" v-if="canEditUser(user)">
                <ds-button
                  v-if="user.verified === false"
                  @click.stop="showUserEditModal(user, 'resend-confirmation')"
                  icon="email-outline" size="extra-small" label="resend verify email"
                />
                <ds-button
                  v-if="user.team_verified !== false"
                  @click.stop="showUserEditModal(user, 'remove')" icon="remove"
                  size="extra-small" label="remove"
                />
                <template v-else>
                  <ds-button
                    @click.stop="showUserEditModal(user, 'accept')" icon="check"
                    size="extra-small" label="accept"
                  />
                  <ds-button
                    @click.stop="showUserEditModal(user, 'deny')"
                    size="extra-small" label="deny" variant="secondary"
                  />
                </template>
              </div>
            </td>
            <td v-if="displayConversationColumn">
              <ds-button
                v-if="hasExistingConversation(user)" size="extra-small"
                label="open conversation"
                @click="openConversation($event, user)"
              />
              <ds-button
                v-else-if="user.id != userId" size="extra-small"
                label="start conversation"
                @click="startConversation($event, user) && canStartConversation(user)"
              />
            </td>
            <td v-if="displaySubscribedToColumn">{{ subscriptionsOf(user) }}</td>
            <td v-if="displayActiveSubscriptionsColumn">{{ formatActiveSubscriptions(user.active_subscriptions) }}</td>
            <td v-if="displayPackagePlanColumn">
              <DsButton
                v-if="user.role === 'actor' || user.subscribed_plan"
                :label="getSubscribedPackageButtonLabel(user)"
                size="extra-small"
                @click.stop="openUserPackageModal(user)"
              />
            </td>
            <td v-if="displayLastLoginColumn">{{ user.last_activity }}</td>
            <td>{{ user.communities || '-' }}</td>
            <td v-if="displayContentActivityColumn">{{ user.content_activity }}</td>
            <td v-if="displayAnnouncementActivityColumn">{{ user.announcement_activity }}</td>
            <td v-if="displayActorActivityColumn">{{ user.actor_activity }}</td>
            <td v-if="displayLastInvitationSentColumn"></td>
          </template>
          <template v-else>
            <td>
              invited user
            </td>
            <td>
              {{ normaliseRole(user.role) }}
            </td>
            <td><a :href="'mailto:' + user.email">{{ user.email }}</a></td>
            <td>
              <div class="users__action-buttons" v-if="canEditInvitation(user)">
                <ds-button
                  @click="showUserEditModal(user, 'cancel-invitation')"
                  icon="remove" size="extra-small" label="cancel"
                />
                <ds-button
                  @click="showUserEditModal(user, 'resend-invitation')"
                  icon="email-outline" size="extra-small" label="resend"
                />
              </div>
            </td>
            <td v-if="displayConversationColumn">
            </td>
            <td v-if="displaySubscribedToColumn">
            </td>
            <td v-if="displayActiveSubscriptionsColumn"></td>
            <td v-if="displayPackagePlanColumn"></td>
            <td v-if="displayLastLoginColumn">
            </td>
            <td v-if="displayContentActivityColumn"></td>
            <td v-if="displayAnnouncementActivityColumn"></td>
            <td v-if="displayActorActivityColumn"></td>
            <td v-if="displayLastInvitationSentColumn">{{ timeDifference(user.created_at) }}</td>
            <!-- as the user object is coming from the invitations, use the created_at value to display the last invitation sent date-->
            <td>-</td>
          </template>
        </tr>
        </tbody>
      </table>
    </div>
    <pagination class="simple-pagination--bottom" :paging="paging" @change="setOffset"/>
  </div>
</template>

<script>
  import _throttle from 'lodash/throttle'

  import Checkbox from '../Form/Checkbox.vue'
  import Pagination from '../Pagination/SimplePagination.vue'
  import ThSort from './ThSort.vue'
  import Badge from '../../components/Badge/Badge.vue'
  import OverlayPlusButton from '../../components/Overlay/OverlayPlusButton.vue'

  import { fetchUserDetail, fetchUsers } from '../../api/config'
  import querify from '../../util/querify'

  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import MODAL_IDS from '../../constants/modal-ids'

  import { Conversations } from '../../api/conversations.js'
  import { asyncConfig } from '../../api/config.js'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '../../store/modules/conversations.js'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import moment from 'moment'
  import CommunityDropdown from '../Communities/CommunityDropdown.vue'
  import Loading from '../Dashboard/ConceptMap/Loading.vue'
  import { ConversationsMixin } from '../../util/ConversationsMixin.js'
  import { defineComponent } from 'vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    props: {
      initialCommunity: {
        type: Number,
        default: null
      }
    },
    data () {
      return {
        fullScroll: false,
        users: [],
        form: {},
        paging: {},
        numberOfResults: null,
        loading: false,
        filters: {
          limit: 100,
          offset: null,
          q: null,
          verified: null,
          claimed: null,
          order: null,
          role: null,
          active_subscription: null,
          community: this.initialCommunity || (this.$route && this.$route.query && this.$route.query.community),
        },
        orderByProperty: null,
        orderByDirection: '',
      }
    },
    methods: {
      canEditUser (user) {
        if (this.isOwner) {
          return (this.isDeveloper ||
            this.isMember
          ) && user.id !== this.userId

        }

        if (this.isMember) {
          return (this.isDeveloper ||
              this.isMember
            ) && user.id !== this.$store.getters.userId &&
            user.role !== 'member' && user.role !== 'owner'
        }

        return false
      },
      openUserPackageModal (user, $event) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { user })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_USER_ECOSYSTEM_MEMBER_PACKAGE)
      },
      getSubscribedPackageButtonLabel (user) {
        if (!user.subscribed_plan) {
          return 'No package'
        }

        if (user.subscribed_plan.length > 20) {
          return user.subscribed_plan.slice(0, 20) + '...'
        }

        return user.subscribed_plan
      },
      normaliseRole (selectedRole) {
        switch (selectedRole) {
          case 'actor':
            return 'Ecosystem Member'
          case 'portfolio_member':
            return 'Portfolio Member'
          case 'owner':
            return 'Owner'
          case 'member':
            return 'Team Member'
          default :
            return ''
        }
      },
      updateCommunityFilter (community) {
        if (!community) {
          this.filters.community = null

          return
        }

        this.filters.community = community.value
        this.users = []
        this.numberOfResults = null
      },
      showAddUserPanel () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, { component: 'invite-users-panel' })
      },
      showAddUserModal () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_USER)
      },
      hasExistingConversation (user) {
        return !!user.conversation_id
      },
      openConversation ($event, user) {
        $event.stopPropagation()
        this.$router.push('/profile/conversations/' + user.conversation_id)
      },
      startConversation ($event, user) {
        $event.stopPropagation()
        Conversations.post({ recipient_id: user.id }).then((conversation) => {
          this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push('/profile/conversations/' + conversation.id).then(() => {
            this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
              component: 'user-information-side-panel',
              metaData: {
                userId: user.id,
                allowDelete: false,
              },
            })
          })
        })
      },
      canEditInvitation (invitation) {
        return this.$store.getters.isOwner
      },
      subscriptionsOf (user) {
        return user.settings ? Object.keys(user.settings).map(key => {
          return user.settings[key] && key
        })
          .filter(key => ['newsletter', 'reports', 'weeklyDigest'].includes(key))
          .map(key => key === 'weeklyDigest' ? 'Weekly Digest' : key)
          .map(key => key === 'newsletter' ? 'Newsletter' : key)
          .join(', ') : ''
      },
      formatActiveSubscriptions (activeSubscriptions) {
        if (!activeSubscriptions) {
          return '-'
        }

        // Humanise raw subscription types, i.e. "transition_reports (trial)" -> "Transition Reports (trial)"
        return activeSubscriptions
          .split(', ')
          .map(entry => {
            const isTrial = entry.endsWith(' (trial)')
            const type = isTrial ? entry.slice(0, -' (trial)'.length) : entry
            const label = type
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')

            return isTrial ? `${label} (trial)` : label
          })
          .join(', ')
      },
      showUserEditModal (user, action) {
        let promise = Promise.resolve(user)

        if (action === 'remove') {
          promise = fetchUserDetail(user.id, 'user-management')
        }

        promise.then(user => {
          this.$store.commit('UI/SET_MODAL_CONTEXT', { user: user, action })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, action === 'remove' ? MODAL_IDS.REMOVE_USER_FROM_ECOSYSTEM : MODAL_IDS.USER_EDIT)
        }).catch(() => {
          this.$store.commit('UI/SET_MODAL_CONTEXT', { user, action })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, action === 'remove' ? MODAL_IDS.REMOVE_USER_FROM_ECOSYSTEM : MODAL_IDS.USER_EDIT)
        })
      },
      timeDifference (createdAtDate) {
        if (!createdAtDate) {
          return
        }

        const start = moment(createdAtDate, 'YYYY/MM/DD')
        const end = moment()

        const diffInDays = end.diff(start, 'days')

        if (diffInDays === 0) {
          return this.$t('timespan_today')
        }

        if (diffInDays === 1) {
          return this.$t('yesterday')
        }

        return this.$t('approx_days_ago', { total: diffInDays })
      },
      exportCsv () {
        document.location = `/api/settings/users?${querify(this.filters)}&export=true`
      },
      clearFilters () {
        this.filters.verified = null
        this.filters.claimed = null
        this.filters.q = null
        this.filters.role = null
        this.filters.active_subscription = null
      },
      setOffset (offset) {
        this.filters = Object.assign({}, this.filters, { offset })
      },
      fetch () {
        this.loading = true

        fetchUsers(this.filters)
          .then((response) => {
            this.loading = false
            this.users = response.data
            this.paging = response.paging
            this.numberOfResults = response.total
          })
          .catch(error => {
            this.loading = false
          })

        this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
      },
      orderBy (property) {
        if (this.orderByProperty === property) {
          this.orderByDirection = (this.orderByDirection === '') ? '-' : ''
        } else {
          this.orderByProperty = property
        }

        this.filters.order = this.orderByDirection + this.orderByProperty
      },
      openActorSidePanel (actorId) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'scores',
          metaData: actorId,
        })
      },
      openUserSidePanel (user) {
        // Only open the side panel if it's a registered user, not an invitation
        if (!user.invited_user) {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'user-information-side-panel',
            metaData: {
              userId: user.id,
              allowDelete: true,
            }
          })
        }
      },
    },
    computed: {
      hasAccessToNewCommunities () {
        return this.$store.getters.hasAccessToCommunities
      },
      canAddEcosystemMembers () {
        return this.$store.getters.isPublisherEnabled && (this.isMember || this.isOwner)
      },
      conversations () {
        return this.$store.state.conversations.listData.data
      },
      canShowConversations () {
        if (!this.hasAccessToConversations) {
          return false
        }

        return this.conversations.length > 0 || !this.$store.state.conversations.listData.loading
      },
      hasAccessToEcosystemMemberPackages () {
        return this.$store.getters.hasAccessToEcosystemMemberPackages
      },
      filterOptions () {
        return {
          role: [{
            'text': 'Show all',
            'value': null,
          }].concat(this.userRoles || []),
          verified: [
            { 'text': 'Show all', 'value': null },
            { 'text': 'Is verified', 'value': true },
            { 'text': 'Is unverified', 'value': false },
          ],
          claimed: [
            { 'text': 'Show all', 'value': null },
            { 'text': 'Has claimed', 'value': true },
            { 'text': 'Has not claimed', 'value': false },
          ],
          activeSubscription: [
            { 'text': 'Show all', 'value': null },
            { 'text': 'Transition Reports (paid)', 'value': 'transition_reports' },
            { 'text': 'Transition Reports (trial)', 'value': 'transition_reports:trial' },
          ],
        }
      },
      filtersAsString () {
        const aFilters = []

        if (this.filters.verified !== null) {
          aFilters.push(this.filters.verified ? 'Is verified: yes' : 'Is verified: no')
        }
        if (this.filters.claimed !== null) {
          aFilters.push(this.filters.claimed ? 'Has claimed: yes' : 'Has claimed: no')
        }
        if (this.filters.role !== null) {
          const roleMapping = this.allowedRoles.filter(map => map.value == this.filters.role)

          if (roleMapping.length == 1) {
            aFilters.push('Role: ' + roleMapping[0].text)
          }
        }
        if (this.filters.active_subscription !== null) {
          const subscriptionOption = this.filterOptions.activeSubscription.find(option => option.value === this.filters.active_subscription)

          aFilters.push('Active subscription: ' + (subscriptionOption ? subscriptionOption.text : this.filters.active_subscription))
        }
        if (this.filters.q !== null && this.filters.q !== '') {
          aFilters.push('Search: ' + this.filters.q)
        }

        if (aFilters.length === 0) {
          return ''
        }

        return ' - ' + aFilters.join(', ')
      },
      hasAccessToKnowledgeBase () {
        return this.$store.getters.hasAccessToKnowledgeBase && this.config.viewDashboard && this.config.viewDashboard.includes('knowledge-base')
      },
      config () {
        return this.$store.state.config
      },
      displayConversationColumn () {
        return this.canShowConversations
      },
      displaySubscribedToColumn () {
        return this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager
      },
      displayActiveSubscriptionsColumn () {
        return this.isOwner || this.isMember
      },
      displayPackagePlanColumn () {
        return this.hasAccessToEcosystemMemberPackages && (this.isOwner || this.isMember)
      },
      displayLastLoginColumn () {
        return this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager
      },
      displayContentActivityColumn () {
        return this.hasAccessToKnowledgeBase && (this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager)
      },
      displayAnnouncementActivityColumn () {
        return this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager
      },
      displayActorActivityColumn () {
        return this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager
      },
      displayLastInvitationSentColumn () {
        return this.isOwner || this.isMember || this.isCommunityManagerAndPortfolioManager
      },
    },
    mounted () {
      this.fetch()
      this.$router.replace({}) // The users page can have some query params that are being used as on-load filters, after fetching data we reset those query params
      this.$bus.on('userUpdated', () => this.fetch())
      this.$bus.on('userDeleted', () => this.fetch())
      this.$bus.on('invitationsAdded', () => this.fetch())
    },
    mixins: [asyncConfig, ConversationsMixin, TranslationsMixin, UserMixin],
    beforeUnmount () {
      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.$bus.off('userUpdated')
      this.$bus.off('userDeleted')
      this.$bus.off('invitationsAdded')
    },
    watch: {
      filters: {
        deep: true,
        handler: _throttle(function () {
          this.fetch()
        }, 300, {
          trailing: true,
        }),
      },
    },
    components: {
      Loading,
      CommunityDropdown,
      Badge,
      ThSort,
      Checkbox,
      Pagination,
      OverlayPlusButton,
    },
  })
</script>

<style scoped>
  .users__cta-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
  }

  .users__button-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }

  .users__results-text {
    flex: 1;
    min-width: 200px;
  }

  .users__action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
  }

  .users-list {
    padding-bottom: 49px;
  }

  /* Mobile responsive styles */
  @media (max-width: 768px) {
    .users__cta-container {
      flex-direction: column;
      align-items: stretch;
    }

    .users__button-group {
      width: 100%;
      flex-direction: column;
    }

    .users__button-group > * {
      width: 100%;
    }

    .users__results-text {
      width: 100%;
      margin-top: 0.5rem;
      text-align: center;
    }

    .users__action-buttons {
      flex-direction: column;
      gap: 0.5rem;
    }

    .users__action-buttons > * {
      width: 100%;
    }
  }

  /* Tablet responsive styles */
  @media (min-width: 769px) and (max-width: 1024px) {
    .users__button-group {
      flex: 1;
    }

    .users__results-text {
      flex: 0 1 auto;
      min-width: auto;
    }
  }

  @-webkit-keyframes expandAnimation {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @-moz-keyframes expandAnimation {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @-o-keyframes expandAnimation {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @keyframes expandAnimation {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @-webkit-keyframes collapseAnimation {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @-moz-keyframes collapseAnimation {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @-o-keyframes collapseAnimation {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @keyframes collapseAnimation {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .panelExpanded {
    -webkit-animation: expandAnimation .5s forwards;
    -moz-animation: expandAnimation .5s forwards;
    -o-animation: expandAnimation .5s forwards;
    animation: expandAnimation .5s forwards;
  }

  .panelCollapsed {
    -webkit-animation: collapseAnimation .5s forwards;
    -moz-animation: collapseAnimation .5s forwards;
    -o-animation: collapseAnimation .5s forwards;
    animation: collapseAnimation .5s forwards;
  }
</style>
